// Pure helpers for the US 10Y real yield (TIPS) tracker. Client-safe and
// unit-tested; the server function in real-yield.functions.ts only fetches.

export type RealYieldDay = { date: string; real: number | null; nominal: number | null };

export type RealYieldMonth = {
  period: string; // YYYY-MM
  realAvg: number | null; // average daily 10Y real yield in the month
  realChange: number | null; // change vs prior month average, percentage points
  goldPct: number | null; // XAUUSD monthly return
};

export type RealYieldRegime = "supportive" | "neutral" | "headwind";

type FredObservation = { date: string; value: string };

/** Parse FRED observations into ascending { date, value } rows, skipping gaps ("."). */
export function parseFredObservations(obs: FredObservation[] | undefined): { date: string; value: number }[] {
  return (obs ?? [])
    .filter((o) => o.value && o.value !== ".")
    .map((o) => ({ date: o.date, value: Number(o.value) }))
    .filter((o) => Number.isFinite(o.value))
    .sort((a, b) => (a.date < b.date ? -1 : 1));
}

/** Merge real and nominal daily series on date, ascending. */
export function mergeDaily(
  real: { date: string; value: number }[],
  nominal: { date: string; value: number }[],
): RealYieldDay[] {
  const nom = new Map(nominal.map((n) => [n.date, n.value]));
  return real.map((r) => ({ date: r.date, real: r.value, nominal: nom.get(r.date) ?? null }));
}

/** Average the daily real yield per calendar month and attach gold monthly returns. */
export function buildMonths(days: RealYieldDay[], goldByMonth: Map<string, number>): RealYieldMonth[] {
  const acc = new Map<string, { sum: number; n: number }>();
  for (const d of days) {
    if (d.real == null) continue;
    const p = d.date.slice(0, 7);
    const a = acc.get(p) ?? { sum: 0, n: 0 };
    a.sum += d.real;
    a.n += 1;
    acc.set(p, a);
  }
  const periods = [...acc.keys()].sort();
  return periods.map((p, i) => {
    const a = acc.get(p)!;
    const avg = Math.round((a.sum / a.n) * 100) / 100;
    const prevAvg = i > 0 ? (() => { const pa = acc.get(periods[i - 1])!; return Math.round((pa.sum / pa.n) * 100) / 100; })() : null;
    return {
      period: p,
      realAvg: avg,
      realChange: prevAvg != null ? Math.round((avg - prevAvg) * 100) / 100 : null,
      goldPct: goldByMonth.get(p) ?? null,
    };
  });
}

/**
 * Institutional regime read for gold. Real yields are gold's opportunity
 * cost: deeply negative or clearly falling real yields are supportive,
 * high and rising real yields are a headwind.
 */
export function classifyRegime(level: number, monthlyChange: number | null): RealYieldRegime {
  const falling = monthlyChange != null && monthlyChange <= -0.1;
  const rising = monthlyChange != null && monthlyChange >= 0.1;
  if (level < 0 || (level < 1 && falling)) return "supportive";
  if (level >= 1.5 && rising) return "headwind";
  if (level >= 2) return "headwind";
  return "neutral";
}

/** Change in percentage points over the last n observations. */
export function changeOver(days: RealYieldDay[], n: number): number | null {
  const vals = days.filter((d) => d.real != null);
  if (vals.length <= n) return null;
  const now = vals[vals.length - 1].real!;
  const then = vals[vals.length - 1 - n].real!;
  return Math.round((now - then) * 100) / 100;
}
